<!-- 로그인 -->
<script setup lang="ts">
import FloatingConfigurator from '@/components/FloatingConfigurator.vue'
import LocaleSwitch from '@/components/LocaleSwitch.vue'
import { validate } from '@/modules/validation'
import { useMemberStore } from '@/stores/member'
import { useSiteStore } from '@/stores/site'
import type { FormSubmitEvent } from '@primevue/forms'
import { computed, onMounted, reactive, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import AppCompanyFooter from '../layouts/AppCompanyFooter.vue'

const route = useRoute()
const memberStore = useMemberStore()
const siteStore = useSiteStore()
const { t } = useI18n() // useI18n 추가
const isDevelopment = import.meta.env.DEV

const $viewModel = reactive({
  isSubmitted: false,
  loginId: '',
  password: '',
  isRememberMeChecked: false,
  loginError: null as string | null
})

const joinUrl = computed(() => {
  // URL 생성 로직은 유지
  const currentUrl = new URL(window.location.href)
  currentUrl.pathname = currentUrl.pathname.replace('/login', '/join')
  return currentUrl.toString()
})

const findAccountUrl = computed(() => {
  // URL 생성 로직은 유지
  const currentUrl = new URL(window.location.href)
  currentUrl.pathname = currentUrl.pathname.replace('/login', '/find-account')
  return currentUrl.toString()
})

const handlers = {
  // fn -> handlers 로 변경
  resolver() {
    const errors = {
      loginId: validate($viewModel.loginId).required(t('validation.loginIdRequired')).getErrors(), // t() 사용
      password: validate($viewModel.password).required(t('validation.passwordRequired')).getErrors() // t() 사용
    }

    return { errors }
  },

  goHome() {
    const redirect = siteStore.siteConfig.basePath ?? route.query.redirect
    location.href = redirect as string
  },

  async tryLogin() {
    if (common.isProgressActive.value) return

    common.showProgress()

    const errorMessage = await memberStore.login($viewModel.loginId, $viewModel.password, $viewModel.isRememberMeChecked)
    console.log(errorMessage)

    common.hideProgress()

    if (errorMessage === '') {
      await siteStore.initialize(true)
      handlers.goHome() // fn -> handlers
    } else {
      // 로그인 실패 메시지도 번역 가능하게 처리 (memberStore에서 반환하는 메시지가 키라고 가정)
      // 만약 memberStore가 고정 문자열을 반환한다면, 여기서 t()로 감싸거나 memberStore 수정 필요
      $viewModel.loginError = t(`error.${errorMessage}`) // 예시: t(errorMessage)
    }
  },

  async onFormSubmit(event: FormSubmitEvent) {
    $viewModel.isSubmitted = true
    if (common.isProgressActive.value) return

    if (!event.valid) {
      common.focusToError('#loginForm')
    } else {
      await handlers.tryLogin() // fn -> handlers
    }
  }
}

watch(
  () => $viewModel.loginId,
  () => {
    $viewModel.loginError = null
  }
)

watch(
  () => $viewModel.password,
  () => {
    $viewModel.loginError = null
  }
)

if (!memberStore.loginMember.isEmpty()) {
  handlers.goHome() // fn -> handlers
}

onMounted(() => {
  if (route.query.prefill) {
    $viewModel.loginId = route.query.prefill as string
    document.getElementById('userPw')?.focus()
  }
})

const logo = computed(() => siteStore.logoUrl ?? common.getDefaultLogo())
</script>

<template>
  <FloatingConfigurator v-if="isDevelopment" />
  <div class="bg-surface-50 dark:bg-surface-950 flex items-center justify-center min-h-screen min-w-[100vw] overflow-hidden">
    <div class="flex flex-col items-center justify-center">
      <div class="w-full bg-surface-0 dark:bg-surface-900 py-20 px-8 sm:px-20 signin-box shadow-xl relative">
        <div class="absolute top-5 right-5">
          <LocaleSwitch />
        </div>

        <div class="flex items-center justify-center mb-8">
          <img class="md:max-w-[26rem] md:max-h-[3.8rem]" :src="logo" :alt="siteStore.siteConfig.siteName" />
        </div>

        <Form
          id="loginForm"
          v-slot="$form"
          :validate-on-value-update="$viewModel.isSubmitted"
          :resolver="handlers.resolver"
          @submit="handlers.onFormSubmit">
          <div class="mb-5">
            <label for="loginId" class="block text-surface-900 dark:text-surface-0 text-xl font-medium mb-2 required">
              {{ t('label.loginId') }}
            </label>
            <InputText
              id="loginId"
              v-model="$viewModel.loginId"
              v-keyfilter="/[\w\d\-\.]+/"
              name="loginId"
              type="text"
              :placeholder="t('placeholder.loginId')"
              class="w-full mb-2"
              pattern="[\w\d\-\.]+"
              :title="t('title.loginIdPattern')" />
            <Message v-if="$form.loginId?.invalid" severity="error" size="small" variant="simple">
              {{ $form.loginId.error?.message }}
            </Message>
          </div>

          <div class="mb-5">
            <label for="userPw" class="block text-surface-900 dark:text-surface-0 font-medium text-xl mb-2 required">
              {{ t('label.password') }}
            </label>
            <Password
              v-model="$viewModel.password"
              input-id="userPw"
              name="password"
              :placeholder="t('placeholder.password')"
              :toggle-mask="true"
              class="mb-2"
              fluid
              :feedback="false"
              @keydown.enter="handlers.tryLogin"></Password>
            <Message v-if="$form.password?.invalid" severity="error" size="small" variant="simple">
              {{ $form.password.error?.message }}
            </Message>
          </div>

          <p v-if="$viewModel.loginError !== null" class="text-red-600 font-bold mb-4">{{ $viewModel.loginError }}</p>

          <div class="flex items-center justify-between mt-2 mb-4 gap-8 max-xs:block">
            <div class="flex items-center">
              <Checkbox v-model="$viewModel.isRememberMeChecked" binary class="mr-2" input-id="rememberMe"></Checkbox>
              <label for="rememberMe">{{ t('label.rememberMe') }}</label>
            </div>

            <div class="flex items-end justify-end">
              <a class="font-medium no-underline ml-2 text-right cursor-pointer text-primary" :href="joinUrl">
                {{ t('label.join') }}
              </a>
              <span class="ms-2 text-surface-400 cursor-default">|</span>
              <a class="font-medium no-underline ml-2 text-right cursor-pointer text-primary" :href="findAccountUrl">
                {{ t('label.findAccount') }}
              </a>
            </div>
          </div>
          <Button type="submit" :label="t('label.login')" size="large" fluid></Button>
          <p v-if="siteStore.contactInfo" class="mt-3 text-center">
            {{ t('message.contactSupportLogin') }}
            <strong>{{ siteStore.contactInfo }}</strong>
          </p>
        </Form>

        <div class="mt-7">
          <AppCompanyFooter />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.pi-eye {
  transform: scale(1.6);
  margin-right: 1rem;
}

.pi-eye-slash {
  transform: scale(1.6);
  margin-right: 1rem;
}

.signin-box {
  border: 1px solid var(--surface-border);
  border-radius: 30px;
}
</style>

<i18n lang="yaml">
ko:
  label:
    loginId: 아이디
    password: 비밀번호
    rememberMe: 아이디 저장
    join: 회원가입
    findAccount: 아이디/비밀번호 찾기
    login: 로그인
  placeholder:
    loginId: 아이디 입력
    password: 비밀번호 입력
  title:
    loginIdPattern: 영문, 숫자, 밑줄(_), 마침표(.)만 입력 가능합니다
  validation:
    loginIdRequired: 아이디를 입력해주세요
    passwordRequired: 비밀번호를 입력해주세요
  message:
    contactSupportLogin: '사무국 문의 : '
  # 로그인 실패 관련 메시지 키 (memberStore.login 반환값에 따라 정의 필요)
  error:
    invalidCredentials: 아이디 또는 비밀번호가 올바르지 않습니다.
    accountLocked: 계정이 잠겼습니다. 관리자에게 문의하세요.
    unknownLoginError: 로그인 중 오류가 발생했습니다.
en:
  label:
    loginId: ID
    password: Password
    rememberMe: Remember Me
    join: Sign Up
    findAccount: Find ID/Password
    login: Login
  placeholder:
    loginId: Enter ID
    password: Enter Password
  title:
    loginIdPattern: Only letters, numbers, underscores (_), and periods (.) are allowed
  validation:
    loginIdRequired: Please enter your ID
    passwordRequired: Please enter your password
  message:
    contactSupportLogin: 'Contact. '
  # Login failure message keys (need to be defined based on memberStore.login return value)
  error:
    invalidCredentials: Invalid ID or password.
    accountLocked: Account locked. Please contact administrator.
    unknownLoginError: An error occurred during login.
</i18n>
